import { MenuItemProps } from '@chakra-ui/react';
import { Pagination, PaginationProps } from '@components/pagination';
import { useSnackbar } from '@components/snackbar';
import { EmptyState } from '@sketch-note/design-system/src/components/lessons-list';
import { CreationLoader } from '@sketch-note/design-system/src/components/loaders';
import { EMPTY_STATE_IMAGES } from '@sketch-note/design-system/src/shared/constants';
import { useRouter } from 'next/router';
import * as React from 'react';
import { useEffect } from 'react';
import { API } from 'src/api';
import { useSidebarStore } from 'src/components/sidebar/store';
import { useWorkspaceStore } from 'src/features/workspace/store/useWorkspaceStore';
import { getFileViewName, getFileViewType } from 'src/utils/get-file-type';

import { FileGridItem } from './FileGridItem';
import { FileListItem } from './FileListItem';

interface FilesViewProps {
  files: any[]; // define file type
  menuItems: { label: string; value: string; icon?: JSX.Element }[];
  onClickMenuItem?: (
    menuitem: MenuItemProps,
    e: React.KeyboardEvent<HTMLDivElement>,
    id: string,
    name: string
  ) => void;
  isLoading: boolean;
  mode?: string;
  filterValue?: string;
  view?: string;
  paginationProps?: PaginationProps;
}

export const FilesView = ({
  files,
  menuItems,
  mode,
  onClickMenuItem,
  isLoading,
  filterValue,
  paginationProps,
  view,
}: FilesViewProps): JSX.Element => {
  const router = useRouter();
  const orgId = router?.query?.orgId;
  const orgMode = router?.query?.mode;
  const workspaceId = router?.query?.workspaceId;
  const [openSnackbar] = useSnackbar();
  const { fetchSidebar, pinFile } = useSidebarStore();
  const fetchWorkspace = useWorkspaceStore((s) => s.actions.fetchWorkspace);

  useEffect(() => {
    fetchWorkspace(workspaceId as string);
  }, []);

  function handleOnClick({
    fileId,
    viewId,
    type,
    view,
  }: {
    fileId: string;
    viewId: string;
    type: string;
    view?: string;
  }) {
    router.push(`/${orgMode}/${orgId}/files/${fileId}`);
  }

  const handlePinFile = async (id: string, pin: boolean, name: string) => {
    // find the file by id from files
    const file = files.find((file) => file._id === id);
    if (file) {
      file.pin = pin;

      // update the dashboard store
      pinFile(file, pin);
      // use sidebarStore to pin the file
      openSnackbar({
        text: `${file.title} ${pin ? 'pinned to' : 'unpinned from'} sidebar`,
        snackType: 'standard',
        position: 'bottom-center',
        duration: 1500,
      });
    }
  };
  const handleFavoriteFile = async (id: string, favorite: boolean, name: string) => {
    const response = await API.put(`/files/favorite/${id}`);
    if (response.data.success) {
      fetchSidebar(mode);
      openSnackbar({
        text: `${name} ${favorite ? 'marked as favorite' : 'removed from favorites'}`,
        snackType: 'standard',
        position: 'bottom-center',
        duration: 1500,
      });
    }
  };
  return (
    <div>
      {isLoading
        ? Array(3)
            .fill('data')
            .map((data, index) => <CreationLoader className="mb-15" key={`${data}-${index}`} />)
        : null}
      {!isLoading && (paginationProps?.total || 0) > (paginationProps?.pageSize || 0) ? (
        <div className="flex justify-center my-16">
          <Pagination {...paginationProps} />
        </div>
      ) : null}

      {!isLoading &&
        view === 'list' &&
        files?.map((file, index) => (
          <FileListItem
            name={file?.title || file.file?.name || file.file?.title || 'Untitled'}
            icon={file.file?.icon}
            modified={{ author: file?.updatedBy?.firstname, timing: file?.updatedAt }}
            sharedWith={file.shared}
            workspace={file?.workspace}
            type={getFileViewName(file.filetype)}
            view={file.view === 'default' ? 'kanban' : file.view}
            menuItems={menuItems}
            className="mb-16 docFileShowDiv"
            onClickMenuItem={onClickMenuItem}
            onClick={handleOnClick}
            onPinFile={handlePinFile}
            onFavoriteFile={handleFavoriteFile}
            id={file._id}
            key={`${file._id}-${index}`}
            pinned={file?.pin}
            primaryViewId={file?.file?._id}
            favorite={file?.favorite}
            titleClassName={'docFileTitle'}
          />
        ))}

      <div className="flex flex-wrap gap-24">
        {!isLoading &&
          view === 'grid' &&
          files?.map((file, index) => (
            <FileGridItem
              name={file?.title || file.file?.name || file.file?.title || 'Untitled'}
              icon={file.file?.icon}
              modified={{ author: file.file?.updatedBy?.firstname, timing: file?.updatedAt }}
              sharedWith={file.shared}
              type={getFileViewName(file.filetype)}
              view={file.view === 'default' ? 'kanban' : file.view}
              menuItems={menuItems}
              onClickMenuItem={onClickMenuItem}
              onClick={handleOnClick}
              onPinFile={handlePinFile}
              onFavoriteFile={handleFavoriteFile}
              id={file._id}
              key={`${file._id}-${index}`}
              pinned={file?.pin}
              favorite={file?.favorite}
              primaryViewId={file?.file?._id}
            />
          ))}
      </div>

      {!isLoading && files.length === 0 && mode === 'shared' && filterValue === 'all' ? (
        <EmptyState
          contents={['Sharing is caring! Files shared', 'with your team will be stored here']}
          src={EMPTY_STATE_IMAGES.docsShared}
          thumbnailUrl={
            'https://res.cloudinary.com/samvibes/image/upload/academy/file_walkthrough.png'
          }
          videoId={'guOcr4THRoI'}
        />
      ) : null}
      {!isLoading && files.length === 0 && filterValue === 'archived' ? (
        <EmptyState
          contents={['Hmm, no archived files here']}
          src={EMPTY_STATE_IMAGES.docsArchived}
          thumbnailUrl={
            'https://res.cloudinary.com/samvibes/image/upload/academy/file_walkthrough.png'
          }
          videoId={'guOcr4THRoI'}
        />
      ) : null}
      {!isLoading && files.length === 0 && filterValue === 'deleted' ? (
        <EmptyState
          contents={[
            'No deleted files here yet!',
            'Files in this section will be deleted permanently in 15 days',
          ]}
          className="ml-64"
          src={EMPTY_STATE_IMAGES.docsDeleted}
          thumbnailUrl={
            'https://res.cloudinary.com/samvibes/image/upload/academy/file_walkthrough.png'
          }
          videoId={'guOcr4THRoI'}
        />
      ) : null}

      {!isLoading && (paginationProps?.total || 0) > (paginationProps?.pageSize || 0) ? (
        <div className="flex justify-center my-16">
          <Pagination {...paginationProps} />
        </div>
      ) : null}
    </div>
  );
};
